import {
  ChangeDetectionStrategy,
  Component,
  Signal,
  inject,
  signal,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from '@openng/optimus-ui/button';
import {
  DynamicDialogConfig,
  DynamicDialogRef,
} from '@openng/optimus-ui/dynamicdialog';
import { NoteEditorComponent } from 'src/app/core/components/note-editor.component';
import { ProgressSpinnerComponent } from 'src/app/core/components/progress-spinner.component';
import { CommercialFacilityNote } from 'src/app/model/commercial-services/commercial-facility';
import { CommercialFacilityApiService } from 'src/app/services/api/commercial-facility-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';

export interface CommercialFacilityNoteDialogData {
  facilityId: string;
  note: CommercialFacilityNote | null;
  writeAccess: Signal<boolean>;
}

@Component({
  selector: 'app-commercial-facility-note-dialog',
  imports: [
    ButtonModule,
    NoteEditorComponent,
    ProgressSpinnerComponent,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './commercial-facility-note-dialog.component.html',
  styleUrl: './commercial-facility-note-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommercialFacilityNoteDialogComponent {
  private readonly data = inject(
    DynamicDialogConfig<CommercialFacilityNoteDialogData>
  ).data;
  private readonly ref = inject(DynamicDialogRef);
  private readonly _commercialFacilityApi = inject(
    CommercialFacilityApiService
  );
  private readonly _logAndMessage = inject(LogAndMessageService);

  readonly entryFormWorking = signal(false);
  readonly entryForm = new FormGroup({
    entryText: new FormControl(
      this.data?.note?.note ?? null,
      Validators.required
    ),
  });

  saveEntry(): void {
    const note = this.entryForm.getRawValue().entryText;
    if (
      !this.data?.facilityId ||
      !this.data.writeAccess() ||
      !note ||
      this.entryForm.invalid ||
      this.entryFormWorking()
    ) {
      return;
    }

    this.entryFormWorking.set(true);
    const editing = this.data.note;
    const request = editing
      ? this._commercialFacilityApi.updateCommercialFacilityNote(editing.id, {
          note,
        })
      : this._commercialFacilityApi.createCommercialFacilityNote({
          commercialFacilityId: this.data.facilityId,
          note,
        });

    request.subscribe({
      next: () => {
        this.entryFormWorking.set(false);
        this.ref.close(true);
      },
      error: (error) => {
        console.error(error);
        this.entryFormWorking.set(false);
        this._logAndMessage.translateToErrorAlert({
          headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          bodyKey:
            'COMMERCIAL_SERVICES.MESSAGES.ERROR.SAVE_COMMERCIAL_FACILITY_NOTE',
        });
      },
    });
  }

  cancel(): void {
    this.ref.close();
  }
}
